import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getTaskService } from '@src/container';
import type { TaskDTO, UpdateTaskStatusDTO } from '@src/application/tasks/dto';

interface UpdateTaskStatusVariables {
    taskId: string;
    updateData: UpdateTaskStatusDTO;
}

/**
 * Hook: useUpdateTaskStatus
 * Mutation hook for updating task status (e.g., rescheduling failed tasks)
 * 
 * @param options - React Query mutation options
 * @returns Mutation result
 * 
 * @example
 * ```typescript
 * const { mutate: updateStatus, isPending } = useUpdateTaskStatus({
 *   onSuccess: () => {
 *     toast.success('Task status updated');
 *   }
 * });
 * 
 * updateStatus({ 
 *   taskId: 'task-123', 
 *   updateData: { status: 'pending' } 
 * });
 * ```
 */
export function useUpdateTaskStatus(
    options?: Omit<
        UseMutationOptions<TaskDTO, Error, UpdateTaskStatusVariables>,
        'mutationFn'
    >
) {
    const taskService = getTaskService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async ({ taskId, updateData }: UpdateTaskStatusVariables) => {
            return taskService.updateTaskStatus.execute(taskId, updateData);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate and refetch task queries
            await queryClient.invalidateQueries({ queryKey: ['tasks'] });
            await queryClient.invalidateQueries({ queryKey: ['tasks', variables.taskId] });

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
